import { createContext, useContext, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  SafeMindsLogo,
  cn,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Button,
  useTheme,
} from '@safe-minds-ai/ui';
import {
  LayoutDashboard,
  Users,
  Flag,
  ShieldAlert,
  CreditCard,
  LifeBuoy,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import { logout } from '../api';

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
};

type NavSection = {
  title: string;
  items: NavItem[];
};

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Overview',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/users', label: 'Users', icon: Users },
    ],
  },
  {
    title: 'Operations',
    items: [
      { to: '/flags', label: 'Flags', icon: Flag },
      { to: '/plans', label: 'Plans', icon: CreditCard },
      { to: '/support', label: 'Support', icon: LifeBuoy },
    ],
  },
];

const LOGOUT_ITEM = { label: 'Logout', icon: LogOut };

export const SidebarContext = createContext(true);

export function Sidebar({ children }: { children?: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const activePath = useMemo(() => location.pathname, [location.pathname]);
  const isCollapsed = useContext(SidebarContext);
  const { resolvedTheme } = useTheme();
  const logoVariant = resolvedTheme === 'dark' ? 'full-dark' : 'full-light';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside
      className={cn(
        'relative flex min-h-screen flex-col border-r border-slate-200/60 bg-gradient-to-b from-white/90 via-white/80 to-[#eef3ff]/80 text-slate-600 shadow-[0_30px_80px_-60px_rgba(30,64,175,0.35)] backdrop-blur-2xl transition-[width] duration-300 dark:border-slate-700/50 dark:from-slate-950/90 dark:via-slate-900/90 dark:to-slate-900/80 dark:text-slate-200',
        isCollapsed ? 'w-[72px] px-2 py-6' : 'w-[260px] px-5 py-8'
      )}
    >
      <div className="pointer-events-none absolute inset-0 opacity-30 [background-image:radial-gradient(circle,_rgba(148,170,220,0.45)_1px,_transparent_1px)] [background-size:18px_18px] dark:opacity-15" />
      <div
        className={cn(
          'relative rounded-3xl border border-slate-200/60 bg-white/80 px-4 py-4 shadow-inner dark:border-slate-700/50 dark:bg-slate-900/80',
          isCollapsed ? 'flex flex-col items-center gap-2 px-1' : 'flex flex-col gap-3'
        )}
      >
        <div className={cn('flex flex-col items-center gap-2 text-center')}>
          <SafeMindsLogo
            width={isCollapsed ? 36 : 140}
            variant={isCollapsed ? 'mark-only' : logoVariant}
          />
          {!isCollapsed && (
            <div className="text-center">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Admin Control</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Administrator</p>
            </div>
          )}
        </div>
      </div>
      <div className={cn('absolute -right-4', isCollapsed ? 'top-24' : 'top-20')}>
        {children}
      </div>
      <nav className="mt-8 flex flex-1 flex-col gap-6">
        {NAV_SECTIONS.map((section) => (
          <div key={section.title}>
            {!isCollapsed && (
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-400 dark:text-slate-400">
                  {section.title}
                </p>
                {section.title === 'Operations' ? (
                  <span className="text-xs text-slate-400 dark:text-slate-400">⌄</span>
                ) : null}
              </div>
            )}
            <div className="mt-2 flex flex-col gap-1">
              {section.items.map((item) => {
                const isActive = activePath.startsWith(item.to);
                const baseClasses = cn(
                  'group flex items-center rounded-full text-sm font-semibold transition-all duration-200',
                  isCollapsed ? 'justify-center py-2.5' : 'gap-3 px-3 py-2.5'
                );
                const Icon = item.icon;
                const iconClasses = cn(
                  'h-4 w-4 transition-colors',
                  isActive
                    ? 'text-white'
                    : 'text-slate-500 group-hover:text-slate-900 dark:text-slate-300 dark:group-hover:text-white'
                );
                const labelClasses = cn(
                  'overflow-hidden text-sm transition-all duration-200',
                  isCollapsed ? 'w-0 opacity-0' : 'w-full opacity-100'
                );

                const link = (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={cn(
                      baseClasses,
                      isActive
                        ? 'bg-[#1E3A8A] text-white shadow-[0_18px_40px_-20px_rgba(30,58,138,0.6)]'
                        : 'text-slate-500 hover:bg-white hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800/80 dark:hover:text-white'
                    )}
                  >
                    <Icon className={iconClasses} />
                    <span className={labelClasses}>{item.label}</span>
                  </NavLink>
                );

                return isCollapsed ? (
                  <Tooltip key={item.to}>
                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{item.label}</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  link
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="pt-6">
        {(() => {
          const logoutButton = (
            <Button
              onClick={handleLogout}
              className={cn(
                'w-full rounded-full text-slate-500 transition-colors hover:text-slate-900 dark:text-slate-200 dark:hover:text-white dark:hover:bg-slate-800/80',
                isCollapsed ? 'px-0 py-3' : 'gap-3 px-4 py-3'
              )}
              variant="ghost"
            >
              <LogOut className="h-4 w-4" />
              <span className={cn('text-sm font-semibold', isCollapsed && 'sr-only')}>
                {LOGOUT_ITEM.label}
              </span>
            </Button>
          );
          return isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>{logoutButton}</TooltipTrigger>
              <TooltipContent side="right">
                <p>{LOGOUT_ITEM.label}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            logoutButton
          );
        })()}
      </div>
    </aside>
  );
}
